import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";

export default function Cart() {
  const { items, updateQty, remove, subtotal } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const proceed = () => {
    if (!user) { nav("/login?next=/checkout"); return; }
    nav("/checkout");
  };

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="label-eyebrow">Your bag</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 mb-12">Shopping Bag</h1>

      {items.length === 0 ? (
        <div className="py-20 text-center border-y border-[#E5DFD3]" data-testid="cart-empty">
          <p className="font-display text-3xl">Your bag is empty.</p>
          <p className="text-sm text-[#5C5454] mt-3">Discover heirloom-quality pieces, made to be loved for years.</p>
          <Link to="/shop" data-testid="cart-empty-shop" className="inline-block mt-8 bg-[#6B1A24] text-[#FAFAF7] px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#8A2532] transition-colors">Shop the collection</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 divide-y divide-[#E5DFD3] border-y border-[#E5DFD3]">
            {items.map(i => (
              <div key={i._key} className="py-6 flex gap-6" data-testid={`cart-item-${i.product_id}`}>
                <div className="w-24 h-32 bg-[#F3EFEA] shrink-0">
                  {i.image && <img src={imgSrc(i.image)} alt={i.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-xl">{i.name}</h3>
                      <div className="text-xs text-[#5C5454] mt-1">Size {i.size}{i.color ? ` · ${i.color}` : ""}</div>
                    </div>
                    <button onClick={() => remove(i._key)} data-testid={`cart-remove-${i.product_id}`} className="text-[#5C5454] hover:text-[#6B1A24]"><X size={18} strokeWidth={1.5} /></button>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center border border-[#E5DFD3]">
                      <button onClick={() => updateQty(i._key, i.quantity - 1)} data-testid={`cart-dec-${i.product_id}`} className="p-2 hover:bg-[#F3EFEA]"><Minus size={14} strokeWidth={1.5} /></button>
                      <span className="px-4 text-sm tabular-nums">{i.quantity}</span>
                      <button onClick={() => updateQty(i._key, i.quantity + 1)} data-testid={`cart-inc-${i.product_id}`} className="p-2 hover:bg-[#F3EFEA]"><Plus size={14} strokeWidth={1.5} /></button>
                    </div>
                    <div className="font-display text-xl tabular-nums">${(i.price * i.quantity).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="bg-[#F3EFEA] p-8 h-fit">
            <div className="label-eyebrow">Order Summary</div>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal" className="tabular-nums">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#5C5454]"><span>Shipping</span><span>Calculated at checkout</span></div>
            </div>
            <div className="border-t border-[#D1C7BD] my-6"></div>
            <div className="flex justify-between font-display text-2xl"><span>Total</span><span data-testid="cart-total" className="tabular-nums">${subtotal.toFixed(2)}</span></div>
            <button onClick={proceed} data-testid="cart-checkout-btn" className="mt-8 w-full bg-[#6B1A24] text-[#FAFAF7] py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#8A2532] transition-colors duration-500">Proceed to Checkout</button>
            <Link to="/shop" data-testid="cart-continue" className="block text-center mt-4 text-xs uppercase tracking-[0.25em] text-[#6B1A24]">Continue shopping</Link>
          </aside>
        </div>
      )}
    </div>
  );
}
